/* eslint-disable no-console */
import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Form, Dropdown } from 'react-bootstrap';
import JSON5 from 'json5';

import { CTAButtons, Loader } from 'components';
import {
  deleteConfiguration,
  saveConfiguration,
  updateConfiguration,
} from 'service/ConfigAPI';
import { fetchConfiguration } from 'service/ConfigAPI/requestHandlers';
import { addError, addSuccess } from 'rdx/alert';
import { removeMS, getMSDataByIntegration } from 'rdx/hardware/installer';
import MicroserviceInstallation from 'pages/Hardware/Installer/MicroserviceInstallation';
import { Ctx } from 'components/CtxFormFields/CtxInputs';
import { updateMSConfiguration } from 'service/InstallerApp/requesthandlers';
import MsVersionInput from 'components/Inputs/MsVersionInput';

export function AutoInstallInputField({ entity, value, onChange }) {
  const [focus, setFocus] = useState(false);

  const { valid, description } = useMemo(() => {
    if (!value)
      return {
        valid: true,
        description: 'MS will only be auto-installed if missing',
      };
    if (value === 'latest')
      return {
        valid: true,
        description: 'MS will always be auto-updated to the latest version',
      };
    if (/v\d+\.\d+\.\d+/.test(value))
      return {
        valid: true,
        description: `MS will be auto-updated (or dowgraded) to version ${value}.`,
      };
    return { valid: false, description: '' };
  }, [value]);

  return (
    <Form.Group>
      <Form.Label>Auto-install version</Form.Label>
      <MsVersionInput
        entity={entity}
        value={value}
        onChange={onChange}
        defaultLabel="any"
        InputProps={{
          isValid: valid,
          isInvalid: !valid && !focus,
          onFocus: () => setFocus(true),
          onBlur: () => setFocus(false),
        }}
      >
        <Dropdown.Item eventKey="" active={!value}>
          any
        </Dropdown.Item>
        <Dropdown.Divider />
        <Dropdown.Item eventKey="latest" active={value === 'latest'}>
          latest
        </Dropdown.Item>
      </MsVersionInput>
      <Form.Text muted>{description}</Form.Text>
    </Form.Group>
  );
}

function IntegrationForm({ renderForm, integrationType }) {
  const dispatch = useDispatch();
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { level, level_id, name } = useParams();
  const res = useLocation();
  const isNewIntegration = res?.pathname === 'new';

  const { msIsAvailable, msIsInstalled, entity, installedVersion } =
    useSelector(getMSDataByIntegration(name));

  const navigate = useNavigate();

  // If there is no ms installed, find the latest version and install it
  const [integration, setIntegration] = useState({});
  const [initialIntegrationState, setInitialIntegrationState] = useState({});
  const [integrationState, setIntegrationState] = useState({});
  const [enabledSave, setEnabledSave] = useState(false);
  const [loading, setLoading] = useState(false);

  const updateState = (newState) => {
    setIntegrationState((prevState) => ({
      ...prevState,
      ...newState,
    }));
  };

  const updateEnabledSave = (shouldSave) => {
    if (shouldSave !== enabledSave) {
      setEnabledSave(shouldSave);
    }
  };

  const fetchIntegrationConfig = ({
    newLevel,
    levelId,
    type,
    name: newName,
    lookDeeper,
  }) => {
    setLoading(true);
    fetchConfiguration({
      level: newLevel,
      level_id: levelId,
      type,
      name: newName,
      lookDeeper,
    })
      .then(({ data, err }) => {
        if (!err) {
          setIntegration(data);
          setIntegrationState(data?.value || {});
          setInitialIntegrationState(data?.value || {});
        } else {
          dispatch(addError(err.message));
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!isNewIntegration) {
      fetchIntegrationConfig({
        newLevel: level,
        levelId: level_id,
        type: integrationType,
        name,
        lookDeeper: true,
      });
    }
  }, [integrationType, level, level_id, name]);

  const saveConfig = async () => {
    // If integration exists already(has id) and we are trying to update it(level === integration.level && level_id === integration.level_id) -> save (Ignore levelId if level is "Company")
    // If levels are different , that means we are trying to reuse inherited integration and customize it for the current level

    const levelIDIsMatching =
      integration?.level === 'Company'
        ? true
        : integration?.level_id === level_id;

    const save =
      integration?.id && integration?.level === level && levelIDIsMatching
        ? updateConfiguration
        : saveConfiguration;
    const fixJSON = (conf) =>
      JSON.stringify(JSON5.parse(JSON.stringify(conf)), undefined, 2);

    /*
    update when :
    1. integration has id and level is different form integration level
    2. integration has id

     */

    try {
      await save({
        level,
        level_id,
        type: integrationType,
        name,
        value: fixJSON(integrationState),
      });
      if (msIsInstalled) {
        await updateMSConfiguration(entity, installedVersion, integrationState);
      }
      navigate(
        `/hardware/integrations/${level}/${level_id}/${integrationType}`,
      );
    } catch (err) {
      dispatch(addError(err.message));
    }
  };

  const delIntegration = () => {
    deleteConfiguration({ level, level_id, name, type: integrationType })
      .then(() => {
        navigate(
          `/hardware/integrations/${level}/${level_id}/${integrationType}`,
        );
        dispatch(addSuccess(`${name} integration was successfully deleted`));
      })
      .catch(console.error);
    if (msIsAvailable && msIsInstalled) {
      dispatch(removeMS({ name }));
    }
  };

  const buttons = {
    next: {
      text: 'Save',
      disabled: !enabledSave,
      onClick: () => saveConfig(),
    },
    hardware: {
      text: 'Reset changes',
      onClick: () =>
        fetchIntegrationConfig({
          newLevel: level,
          levelId: level_id,
          type: integrationType,
          newName: name,
          lookDeeper: true,
        }),
    },
    close: {
      text: 'Delete',
      disabled: !integration.name,
      onClick: () => delIntegration(),
    },
  };

  return (
    <div className="d-flex flex-fill flex-column integration-form">
      <Ctx.Provider
        value={useMemo(
          () => ({
            values: {
              level,
              level_id,
              type: integrationType,
              name,
              integrationState,
              initialIntegrationState,
              enabledSave,
              setEnabledSave: updateEnabledSave,
              loading,
            },
            onChange: updateState,
          }),
          [
            level,
            level_id,
            integrationType,
            name,
            integrationState,
            initialIntegrationState,
            enabledSave,
            updateEnabledSave,
            loading,
            updateState,
          ],
        )}
      >
        {entity && <MicroserviceInstallation microService={entity} />}
        {entity && (
          <div className="my-1 text-right">
            <Link to={`/hardware/installer/msconfig/${entity}`}>
              Go to Micro-Service Manager
            </Link>
          </div>
        )}
        {entity && (
          <AutoInstallInputField
            entity={entity}
            value={integrationState.version}
            onChange={(v) => setIntegrationState((c) => ({ ...c, version: v }))}
          />
        )}
        {loading ? <Loader /> : renderForm()}
        <CTAButtons buttons={buttons} />
      </Ctx.Provider>
    </div>
  );
}

IntegrationForm.propTypes = {
  renderForm: PropTypes.func.isRequired,
};

export default IntegrationForm;
